import { CommonModule } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Tags } from 'app/enums/tags.enum';
import { Allergens } from 'app/enums/allergens.enum';
import { Establishment } from 'app/models/establishment';
import { MenuService } from 'app/modules/menu/menu.service';
import { EstablishmentService } from 'app/modules/establishment/establishment.service';
import { MenuItem } from 'app/models/menu-item';
import { LanguagesUtils } from 'app/utils/languages.utils';
import { IconsUtils } from 'app/utils/icons.utils';
import { MenuItemPriceOption } from 'app/models/menu-item-price-option';
import { Subject, takeUntil } from 'rxjs';
import { ResponsiveImageService, ImageSize } from 'app/modules/shared/services/responsive-image.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MenuPricePipe } from 'app/modules/shared/pipes/menu-price.pipe';


@Component({
  selector: 'app-item-details',
  standalone: true,
  imports: [
    CommonModule,
    MenuPricePipe,
    MatTooltipModule,
    TranslocoModule,
    MatProgressSpinnerModule,
    MatIconModule
  ],
  templateUrl: './item-details.component.html',
  styleUrls: ['./item-details.component.scss']
})
export class ItemDetailsComponent implements OnInit, OnDestroy {
  
  establishment: Establishment;
  currentLocale: string;
  currentLanguage: string;
  defaultLanguage: string;
  imageLoaded: boolean = false; // Image loading state
  imageError: boolean = false; // Image error state
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  
  // Expose enum for the template
  ImageSize = ImageSize;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _menuService: MenuService,
    private _establishmentService: EstablishmentService,
    private _translocoService: TranslocoService,
    private responsiveImageService: ResponsiveImageService
  ) {
    this.establishment = this._menuService.establishment;
    this.currentLocale = locale;
  }

  ngOnInit(): void {
    // Subscribe to establishment updates - establishment may not be available
    // on MenuService at dialog creation time, so we use EstablishmentService
    this._establishmentService.establishment$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((establishment) => {
        this.establishment = establishment;
      });

    // Subscribe to language changes
    this._translocoService.langChanges$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((lang) => {
        this.currentLocale = lang;
        // Update current language when language changes
        this.currentLanguage = lang;
      });
      
    // Reset image loading state
    this.imageLoaded = false;

    // Initialize current and default languages
    this.currentLanguage = LanguagesUtils.getLocalLang();
    this.defaultLanguage = this.establishment.establishmentLanguages.find(lang => lang.defaultLanguage)?.languageRefCode;
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  getItemTitle(item: MenuItem): string {
    if (item.notTranslateTitle) {
      const defaultLanguage = this.establishment.establishmentLanguages.find(lang => lang.defaultLanguage);
      return item.values.find(value => value.languageRefCode === defaultLanguage?.languageRefCode)?.name || '';
    }
    return item.values.find(value => value.languageRefCode === LanguagesUtils.getMenuRemoteLang(this.establishment))?.name || '';
  }

  getItemDescription(item: MenuItem): string {
    return item.values.find(value => value.languageRefCode === LanguagesUtils.getMenuRemoteLang(this.establishment))?.description || '';
  }

  getFoodTypeIcon(tag: Tags): string {
    return IconsUtils.getFoodTypeIcon(tag);
  }

  getFoodTypeClasses(tag: Tags): string {
    return IconsUtils.getFoodTypeClasses(tag);
  }

  getAllergenIcon(allergen: Allergens): string {
    return IconsUtils.getAllergenIcon(allergen);
  }

  getAllergenClasses(): string {
    return IconsUtils.getAllergenClasses();
  }

  /**
   * Get only available price options
   * @returns List of available price options
   */
  getAvailablePriceOptions(): MenuItemPriceOption[] {
    return this.data.inputData.priceOptions
      .sort((a, b) => a.position - b.position);
  }

  /**
   * Get the price option name in the current language
   * @param option Price option
   * @returns Translated name of the option
   */
  getPriceOptionName(option: MenuItemPriceOption): string {
    const currentLang = LanguagesUtils.getMenuRemoteLang(this.establishment);
    return option.values.find(value => 
      value.languageRefCode === currentLang
    )?.name || '';
  }

  /**
   * Gets the URL of the image in LARGE size for item details
   * @param item The menu item
   * @returns The URL of the image in LARGE size
   */
  getItemImageUrl(item: MenuItem): string {
    return item.image ? this.responsiveImageService.getImageUrl(item.image, ImageSize.LARGE, this.establishment?.planCode) : '';
  }

  /**
   * Gets the fallback URL (JPEG) for browsers that don't support WebP
   * @param item The menu item
   * @returns The fallback image URL in JPEG format
   */
  getItemFallbackImageUrl(item: MenuItem): string {
    return item.image ? this.responsiveImageService.getFallbackImageUrl(item.image, ImageSize.LARGE, this.establishment?.planCode) : '';
  }

  /**
   * Generates the srcset for responsive images
   * @param item The menu item
   * @returns The value for the srcset attribute
   */
  getItemImageSrcSet(item: MenuItem): string {
    return item.image ? `${this.responsiveImageService.getImageUrl(item.image, ImageSize.LARGE, this.establishment?.planCode)} 1280w` : '';
  }

  /**
   * Gets the item name for the alt attribute
   * @param item The menu item
   * @returns The item name
   */
  getItemName(item: MenuItem): string {
    return item.values.find(value => value.languageRefCode === LanguagesUtils.getMenuRemoteLang(this.establishment))?.name || '';
  }

  /**
   * Handles the image load complete event
   */
  onImageLoad(): void {
    this.imageLoaded = true;
    this.imageError = false;
  }

  /**
   * Handles the image loading error event
   */
  onImageError(): void {
    this.imageLoaded = true; // Stop showing the spinner
    this.imageError = true; // Indicate there was an error
  }

  isNotDefaultLanguage(): boolean {
    if (!this.currentLanguage || !this.defaultLanguage) {
      return false;
    }
    return this.currentLanguage.toLowerCase() !== this.defaultLanguage.toLowerCase();
  }

  getItemNameInDefaultLanguage(item: MenuItem): string {
    const defaultLanguage = this.establishment.establishmentLanguages.find(lang => lang.defaultLanguage);
    return item.values.find(value => value.languageRefCode === defaultLanguage?.languageRefCode)?.name || '';
  }

  /**
   * Determines if the default language name should be displayed for an item
   * Display only when:
   * - Current language is different from default language
   * - AND the item doesn't have notTranslateTitle flag set to true
   * @param item The menu item to check
   * @returns True if default language name should be shown
   */
  shouldShowDefaultLanguageName(item: MenuItem): boolean {
    return this.isNotDefaultLanguage() && !item.notTranslateTitle;
  }

}

